// ============================================
// RESEARCH ENGINE - TOP 10 TRADE IDEA GENERATOR
// Surfaces liquid, mispriced stocks and routes through 7-step framework
// ============================================

import {
  TickerAnalysis,
  ResearchResult,
  ResearchRun,
  ResearchEngineConfig,
  MispricingTag,
  MispricingAnalysis,
  UniverseStock,
  DEFAULT_LIQUID_UNIVERSE
} from '../types';
import { runFullAnalysis } from './apiService';

// ============ CONFIGURATION DEFAULTS ============

export const DEFAULT_RESEARCH_CONFIG: ResearchEngineConfig = {
  universeSource: 'most_active',
  minLiquidityScore: 50,
  minOptionsVolume: 10000,
  maxSpread: 3.0,
  mispricingThreshold: 3,
  includeTags: ['UNDERVALUED', 'OVERVALUED'],
  riskTolerance: 5,
  maxConcurrentAnalysis: 2,  // Respect rate limits
  maxResults: 10,
  sortBy: 'score'
};

// ============ UNIVERSE FETCHING ============

/**
 * Fetch most-active options universe from Perplexity AI
 * Falls back to default liquid universe if API fails
 */
export async function fetchMostActiveUniverse(): Promise<UniverseStock[]> {
  try {
    const response = await fetch('/api/research', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'fetch-universe' })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch universe');
    }

    const data = await response.json();
    if (data.universe && Array.isArray(data.universe)) {
      return data.universe;
    }
  } catch (err) {
    console.warn('Universe fetch failed, using default liquid universe:', err);
  }

  // Fallback to hardcoded liquid universe
  return DEFAULT_LIQUID_UNIVERSE.map(ticker => ({
    ticker,
    name: ticker,
    optionsVolume: 100000,  // Assume high volume for known liquid names
    avgSpread: 1.0,
    marketCap: 100000000000,
    sector: 'Unknown',
    liquidityScore: 80,
    source: 'default'
  }));
}

/**
 * Get universe from user's watchlist
 */
export async function fetchWatchlistUniverse(userId: string): Promise<UniverseStock[]> {
  try {
    const response = await fetch(`/api/watchlist?userId=${userId}`);
    if (!response.ok) throw new Error('Failed to fetch watchlist');

    const data = await response.json();
    return (data.watchlist || []).map((item: { ticker: string }) => ({
      ticker: item.ticker,
      name: item.ticker,
      optionsVolume: 50000,
      avgSpread: 1.5,
      marketCap: 0,
      sector: 'Unknown',
      liquidityScore: 70,
      source: 'watchlist'
    }));
  } catch (err) {
    console.warn('Watchlist fetch failed:', err);
    return [];
  }
}

// ============ MISPRICING ANALYSIS ============

/**
 * Tag a stock as undervalued, overvalued, or fair based on technical/fundamental signals
 * Uses analysis data to compute mispricing score
 */
export function analyzeMispricing(analysis: TickerAnalysis): MispricingAnalysis {
  const { marketData, technicals, optionsData, scores } = analysis;
  const signals = {
    pricePosition52w: 0.5,
    rsiLevel: technicals.rsi,
    ivRankLevel: optionsData.ivRank,
    peRatio: marketData.peRatio,
    sectorComparison: undefined as string | undefined
  };

  const reasoning: string[] = [];
  let mispricingScore = 0;  // -10 (overvalued) to +10 (undervalued)

  // 1. Price position in 52-week range
  if (marketData.high52 && marketData.low52 && marketData.price) {
    const range = marketData.high52 - marketData.low52;
    if (range > 0) {
      signals.pricePosition52w = (marketData.price - marketData.low52) / range;

      if (signals.pricePosition52w < 0.25) {
        mispricingScore += 3;
        reasoning.push(`Near 52-week low (${Math.round(signals.pricePosition52w * 100)}% of range) - potential undervaluation`);
      } else if (signals.pricePosition52w < 0.40) {
        mispricingScore += 2;
        reasoning.push(`Below middle of 52-week range (${Math.round(signals.pricePosition52w * 100)}%)`);
      } else if (signals.pricePosition52w > 0.85) {
        mispricingScore -= 3;
        reasoning.push(`Near 52-week high (${Math.round(signals.pricePosition52w * 100)}% of range) - potential overvaluation`);
      } else if (signals.pricePosition52w > 0.70) {
        mispricingScore -= 1;
        reasoning.push(`In upper portion of 52-week range (${Math.round(signals.pricePosition52w * 100)}%)`);
      }
    }
  }

  // 2. RSI-based momentum (oversold = undervalued signal)
  if (technicals.rsi < 35) {
    mispricingScore += 3;
    reasoning.push(`Oversold RSI (${technicals.rsi.toFixed(0)}) - potential mean reversion opportunity`);
  } else if (technicals.rsi < 45) {
    mispricingScore += 1;
    reasoning.push(`Below-average RSI (${technicals.rsi.toFixed(0)})`);
  } else if (technicals.rsi > 70) {
    mispricingScore -= 3;
    reasoning.push(`Overbought RSI (${technicals.rsi.toFixed(0)}) - extended upside`);
  } else if (technicals.rsi > 60) {
    mispricingScore -= 1;
    reasoning.push(`Above-average RSI (${technicals.rsi.toFixed(0)})`);
  }

  // 3. IV Rank (high IV = premium is "expensive" = options overpriced)
  if (optionsData.ivRank > 70) {
    mispricingScore -= 2;  // High IV often precedes pullback
    reasoning.push(`High IV Rank (${optionsData.ivRank}) - elevated fear/premium`);
  } else if (optionsData.ivRank < 20) {
    mispricingScore += 2;
    reasoning.push(`Low IV Rank (${optionsData.ivRank}) - cheap premium environment`);
  }

  // 4. P/E Ratio vs typical (if available)
  if (marketData.peRatio) {
    if (marketData.peRatio < 12) {
      mispricingScore += 2;
      reasoning.push(`Low P/E ratio (${marketData.peRatio.toFixed(1)}) - value territory`);
    } else if (marketData.peRatio < 18) {
      mispricingScore += 1;
      reasoning.push(`Reasonable P/E (${marketData.peRatio.toFixed(1)})`);
    } else if (marketData.peRatio > 40) {
      mispricingScore -= 2;
      reasoning.push(`Elevated P/E (${marketData.peRatio.toFixed(1)}) - growth pricing`);
    } else if (marketData.peRatio > 30) {
      mispricingScore -= 1;
      reasoning.push(`Above-average P/E (${marketData.peRatio.toFixed(1)})`);
    }
  }

  // 5. Trade score alignment (low score + oversold = strong undervalued signal)
  if (scores.normalizedTotal < 50 && technicals.rsi < 40) {
    mispricingScore += 1;
    reasoning.push('Framework score depressed alongside technicals');
  }

  // Clamp score to -10 to +10
  mispricingScore = Math.max(-10, Math.min(10, mispricingScore));

  // Determine tag
  let tag: MispricingTag = 'FAIR';
  if (mispricingScore >= 3) tag = 'UNDERVALUED';
  else if (mispricingScore <= -3) tag = 'OVERVALUED';

  // Confidence based on signal agreement
  const signalCount = reasoning.length;
  const confidence = Math.min(95, 50 + signalCount * 12);

  return {
    ticker: analysis.ticker,
    tag,
    score: mispricingScore,
    confidence,
    signals,
    reasoning
  };
}

// ============ BATCH ANALYSIS ORCHESTRATION ============

interface BatchProgress {
  processed: number;
  total: number;
  currentTicker: string;
  results: ResearchResult[];
  errors: { ticker: string; error: string }[];
}

/**
 * Run batch analysis on multiple tickers with rate limiting
 */
export async function runBatchAnalysis(
  tickers: string[],
  config: ResearchEngineConfig,
  onProgress: (progress: BatchProgress) => void
): Promise<ResearchResult[]> {
  const results: ResearchResult[] = [];
  const errors: { ticker: string; error: string }[] = [];

  // Process in small batches to respect rate limits
  const batchSize = config.maxConcurrentAnalysis;

  for (let i = 0; i < tickers.length; i += batchSize) {
    const batch = tickers.slice(i, i + batchSize);

    // Run batch in parallel
    const batchPromises = batch.map(async (ticker) => {
      try {
        // Notify progress
        onProgress({
          processed: results.length,
          total: tickers.length,
          currentTicker: ticker,
          results: [...results],
          errors: [...errors]
        });

        // Run full 7-step analysis
        const analysis = await runFullAnalysis(
          ticker,
          () => {},  // Suppress individual progress for batch mode
          config.riskTolerance
        );

        // Compute mispricing
        const mispricing = analyzeMispricing(analysis);

        // Convert to ResearchResult
        const result = convertToResearchResult(analysis, mispricing);
        return result;
      } catch (err: any) {
        console.error(`Analysis failed for ${ticker}:`, err);
        errors.push({ ticker, error: err.message || 'Unknown error' });
        return null;
      }
    });

    const batchResults = await Promise.all(batchPromises);

    // Add successful results
    for (const result of batchResults) {
      if (result) {
        results.push(result);
      }
    }

    // Wait between batches to respect rate limits (2s delay)
    if (i + batchSize < tickers.length) {
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }

  // Final progress update
  onProgress({
    processed: results.length,
    total: tickers.length,
    currentTicker: 'Complete',
    results,
    errors
  });

  return results;
}

/**
 * Convert TickerAnalysis + MispricingAnalysis to ResearchResult
 */
function convertToResearchResult(
  analysis: TickerAnalysis,
  mispricing: MispricingAnalysis
): ResearchResult {
  const bestStrategy = analysis.bestStrategy;

  return {
    ticker: analysis.ticker,
    name: undefined,  // Could be fetched from company profile

    // Mispricing
    mispricingTag: mispricing.tag,
    mispricingScore: mispricing.score,
    mispricingReasoning: mispricing.reasoning,

    // 7-step results
    tradeScore: analysis.scores.normalizedTotal,
    status: analysis.status,
    statusReason: analysis.statusReason,

    // Best strategy
    strategyName: bestStrategy?.name || 'None',
    strategyType: bestStrategy?.type || 'Unknown',
    direction: bestStrategy?.direction || 'NEUTRAL',
    creditDebit: bestStrategy?.creditDebit || 'DEBIT',

    // Risk/reward
    maxProfit: bestStrategy?.maxProfit || 0,
    maxLoss: bestStrategy?.maxLoss || 0,
    breakeven: bestStrategy?.breakeven || 0,
    probabilityOfProfit: bestStrategy?.probabilityOfProfit || 0,
    riskRewardRatio: bestStrategy?.riskRewardRatio || 0,

    // Key metrics
    ivRank: analysis.optionsData.ivRank,
    liquidityGrade: analysis.riskFlags.liquidityGrade,
    daysToEarnings: analysis.riskFlags.earningsInDays,

    // Position sizing
    recommendedContracts: analysis.positionSizing.recommendedContracts,
    capitalRequired: analysis.positionSizing.capitalRequired,

    // Management
    managementNotes: generateManagementNotes(analysis, mispricing),

    // Full analysis for drill-down
    fullAnalysis: analysis,

    // Metadata
    analyzedAt: analysis.analyzedAt
  };
}

/**
 * Generate management guidance notes
 */
function generateManagementNotes(
  analysis: TickerAnalysis,
  mispricing: MispricingAnalysis
): string[] {
  const notes: string[] = [];

  // Direction-based entry
  if (mispricing.tag === 'UNDERVALUED' && analysis.bestStrategy?.direction === 'BULLISH') {
    notes.push('Bullish structure aligned with undervaluation thesis');
  } else if (mispricing.tag === 'OVERVALUED' && analysis.bestStrategy?.direction === 'BEARISH') {
    notes.push('Bearish structure aligned with overvaluation thesis');
  }

  // Risk warnings
  if (analysis.riskFlags.earningsInDays && analysis.riskFlags.earningsInDays <= 14) {
    notes.push(`Earnings in ${analysis.riskFlags.earningsInDays} days - consider post-earnings entry or hedge`);
  }

  if (analysis.riskFlags.fomcInDays && analysis.riskFlags.fomcInDays <= 7) {
    notes.push(`FOMC in ${analysis.riskFlags.fomcInDays} days - expect volatility`);
  }

  // IV-based guidance
  if (analysis.optionsData.ivRank > 70) {
    notes.push('Elevated IV - favor credit strategies or wait for IV crush');
  } else if (analysis.optionsData.ivRank < 25) {
    notes.push('Low IV environment - debit structures more efficient');
  }

  // Exit guidance
  if (analysis.bestStrategy?.management) {
    notes.push(`Target 50% profit at $${analysis.bestStrategy.management.profitTarget50?.toFixed(2) || 'TBD'}`);
    notes.push(`Stop loss if position exceeds ${analysis.bestStrategy.management.maxLossMultiple}x max risk`);
  }

  // Disclaimer
  notes.push('Research idea only - conduct your own due diligence before trading');

  return notes;
}

// ============ RANKING & FILTERING ============

/**
 * Rank and filter results to get top ideas
 */
export function rankResults(
  results: ResearchResult[],
  config: ResearchEngineConfig
): ResearchResult[] {
  // Filter by mispricing tag
  let filtered = results.filter(r =>
    config.includeTags.includes(r.mispricingTag)
  );

  // Filter by trade status (optional - could include CAUTION)
  filtered = filtered.filter(r => r.status !== 'NO_TRADE');

  // Filter by liquidity
  filtered = filtered.filter(r => {
    const gradeScore = { 'A': 100, 'B': 80, 'C': 60, 'D': 40, 'F': 20 }[r.liquidityGrade] || 0;
    return gradeScore >= config.minLiquidityScore;
  });

  // Sort by chosen metric
  filtered.sort((a, b) => {
    switch (config.sortBy) {
      case 'score':
        return b.tradeScore - a.tradeScore;
      case 'mispricing':
        return Math.abs(b.mispricingScore) - Math.abs(a.mispricingScore);
      case 'probability':
        return b.probabilityOfProfit - a.probabilityOfProfit;
      case 'riskReward':
        return b.riskRewardRatio - a.riskRewardRatio;
      default:
        return b.tradeScore - a.tradeScore;
    }
  });

  // Assign ranks
  const ranked = filtered.slice(0, config.maxResults).map((result, index) => ({
    ...result,
    rank: index + 1
  }));

  return ranked;
}

// ============ MAIN RESEARCH ENGINE FUNCTION ============

/**
 * Run the full research engine pipeline:
 * 1. Fetch universe
 * 2. Filter for liquidity
 * 3. Run batch 7-step analysis
 * 4. Tag mispricing
 * 5. Rank and return top 10
 */
export async function runResearchEngine(
  config: Partial<ResearchEngineConfig> = {},
  userId?: string,
  onProgress?: (progress: Partial<ResearchRun>) => void
): Promise<ResearchRun> {
  const fullConfig: ResearchEngineConfig = {
    ...DEFAULT_RESEARCH_CONFIG,
    ...config
  };

  const run: ResearchRun = {
    runName: `Research ${new Date().toISOString().split('T')[0]}`,
    universeSource: fullConfig.universeSource,
    universeCount: 0,
    tickersInUniverse: [],
    tickersAnalyzed: 0,
    tickersPassed: 0,
    results: [],
    topTen: [],
    status: 'running',
    progress: 0,
    createdAt: new Date().toISOString(),
    startedAt: new Date().toISOString()
  };

  try {
    // 1. Fetch universe
    onProgress?.({ ...run, status: 'running', progress: 5 });

    let universe: UniverseStock[];
    if (fullConfig.universeSource === 'manual' && fullConfig.manualTickers) {
      universe = fullConfig.manualTickers.map(ticker => ({
        ticker,
        name: ticker,
        optionsVolume: 50000,
        avgSpread: 1.5,
        marketCap: 0,
        sector: 'Unknown',
        liquidityScore: 70,
        source: 'manual'
      }));
    } else if (fullConfig.universeSource === 'watchlist' && userId) {
      universe = await fetchWatchlistUniverse(userId);
    } else {
      universe = await fetchMostActiveUniverse();
    }

    run.universeCount = universe.length;
    run.tickersInUniverse = universe.map(u => u.ticker);
    onProgress?.({ ...run, progress: 10 });

    // 2. Filter for basic liquidity
    const filteredUniverse = universe.filter(stock =>
      stock.liquidityScore >= fullConfig.minLiquidityScore
    );

    const tickersToAnalyze = filteredUniverse.map(s => s.ticker);
    run.tickersAnalyzed = tickersToAnalyze.length;

    // 3. Run batch analysis
    const batchResults = await runBatchAnalysis(
      tickersToAnalyze,
      fullConfig,
      (batchProgress) => {
        const pct = 10 + Math.round((batchProgress.processed / batchProgress.total) * 80);
        run.progress = pct;
        run.currentTicker = batchProgress.currentTicker;
        run.errors = batchProgress.errors;
        onProgress?.({ ...run });
      }
    );

    run.results = batchResults;
    run.tickersPassed = batchResults.filter(r => r.status !== 'NO_TRADE').length;

    // 4. Rank and get top 10
    run.topTen = rankResults(batchResults, fullConfig);

    run.status = 'completed';
    run.progress = 100;
    run.completedAt = new Date().toISOString();

    onProgress?.({ ...run });

    return run;

  } catch (err: any) {
    run.status = 'failed';
    run.errors = [{ ticker: 'SYSTEM', error: err.message || 'Unknown error' }];
    onProgress?.({ ...run });
    throw err;
  }
}

// ============ AI-ENHANCED UNIVERSE (PERPLEXITY) ============

/**
 * Query Perplexity for current most-active options + mispriced stocks
 * This provides fresh, daily-updated universe based on market activity
 */
export async function fetchAIScreenedUniverse(): Promise<{
  tickers: string[];
  analysis: string;
}> {
  try {
    const response = await fetch('/api/research', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'ai-screen' })
    });

    if (!response.ok) {
      throw new Error('AI screening failed');
    }

    return await response.json();
  } catch (err) {
    console.error('AI screening error:', err);
    return {
      tickers: DEFAULT_LIQUID_UNIVERSE.slice(0, 20),
      analysis: 'Using default liquid universe (AI screening unavailable)'
    };
  }
}
